import React, { useState } from 'react';
import { Compass, CheckCircle, MapPin, User, Mail, Phone, Calendar, PenTool } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const phoneConstraints = {
  '+91': { min: 10, max: 10 },
  '+1': { min: 10, max: 10 },
  '+44': { min: 10, max: 11 },
  '+61': { min: 9, max: 9 },
  '+971': { min: 9, max: 9 },
  '+49': { min: 10, max: 11 },
  '+33': { min: 9, max: 9 },
  '+39': { min: 9, max: 10 },
  '+34': { min: 9, max: 9 },
  '+65': { min: 8, max: 8 },
  '+60': { min: 9, max: 10 },
  '+81': { min: 10, max: 10 },
  '+86': { min: 11, max: 11 },
  '+27': { min: 9, max: 9 },
  '+64': { min: 8, max: 10 },
  '+55': { min: 10, max: 11 },
  '+52': { min: 10, max: 10 },
  '+31': { min: 9, max: 9 },
  '+41': { min: 9, max: 9 },
  '+46': { min: 7, max: 9 },
  '+972': { min: 8, max: 9 },
};

export default function JourneyPlanner() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    place: '',
    days: '',
    description: ''
  });
  const [countryCode, setCountryCode] = useState('+91');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const getErrorStr = (detail, fallback) => {
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
    if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    return fallback;
  };

  const isLoggedIn = localStorage.getItem('user_authenticated') === 'true' || localStorage.getItem('admin_authenticated') === 'true';

  const handleChange = (e) => {
    let value = e.target.value;
    if (e.target.name === 'name') {
      value = value.replace(/[^A-Za-z\s]/g, '');
    }
    if (e.target.name === 'phone') {
      value = value.replace(/\D/g, '');
      const maxLen = phoneConstraints[countryCode]?.max || 15;
      if (value.length > maxLen) {
        return;
      }
    }
    if (e.target.name === 'description') {
      const currentWordCount = value.trim() ? value.trim().split(/\s+/).length : 0;
      if (currentWordCount > 200 && value.length > formData.description.length) {
        return; // Prevent typing more than 200 words
      }
    }
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) {
      setError(t('planner.errNotLoggedIn'));
      return;
    }
    setLoading(true);
    setError('');
    
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: `${countryCode} ${formData.phone}`,
        place: formData.place,
        days: parseInt(formData.days, 10),
        description: formData.description
      };
      
      const res = await fetch(`${apiBase}/api/journeys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (res.status === 429) { 
        setError('Too many requests. Please wait a moment before trying again.');
        return;
      }

      
      if (res.ok) {
        setSuccess(true);
        setFormData({ name: '', email: '', phone: '', place: '', days: '', description: '' });
      } else {
        const data = await res.json();
        setError(getErrorStr(data.detail, t('planner.errSubmit')));
      }
    } catch (err) {
      setError(t('planner.errConnection'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section 
      id="planner" 
      style={{
        padding: '120px 0',
        backgroundColor: 'var(--bg-dark)',
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div 
        style={{
          width: '90%',
          maxWidth: '800px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <span className="text-gold" style={{ textTransform: 'uppercase', letterSpacing: '0.3em', fontSize: '0.8rem', fontWeight: '600' }}>
            {t('planner.header')}
          </span>
          <h2 className="font-serif" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginTop: '10px', color: 'var(--text-light)' }}>
            {t('planner.title')}
          </h2>
          <div style={{ width: '60px', height: '2px', backgroundColor: 'var(--primary-gold)', margin: '20px auto 0' }} />
          <p style={{ marginTop: '20px', color: 'var(--text-muted)', fontSize: '0.95rem', maxWidth: '600px', margin: '20px auto 0' }}>
            {t('planner.description')}
          </p>
        </div>

        <div className="glass-card animate-fade-in planner-card" style={{ padding: '40px', borderRadius: '16px', border: '1px solid rgba(181, 137, 37, 0.2)', boxShadow: '0 20px 50px rgba(41, 33, 26, 0.08)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', background: 'rgba(250, 247, 242, 0.8)' }}>
          {success ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ color: 'var(--primary-gold)', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                <CheckCircle size={64} />
              </div>
              <h3 className="font-serif text-gradient" style={{ fontSize: '2rem', marginBottom: '15px' }}>{t('planner.successTitle')}</h3>
              <p style={{ color: 'var(--text-light)', fontSize: '1rem', lineHeight: '1.6', marginBottom: '30px' }}>
                {t('planner.successMessage')}
              </p>
              <button 
                onClick={() => setSuccess(false)}
                className="btn-gold pulse-gold-button"
                style={{ padding: '12px 30px', borderRadius: '30px', textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.85rem', border: 'none', cursor: 'pointer' }}
              >
                {t('planner.btnAnother')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
              
              {error && (
                <div style={{ padding: '15px', backgroundColor: 'rgba(198, 40, 40, 0.1)', borderLeft: '4px solid var(--accent-terracotta)', color: 'var(--accent-terracotta)', fontSize: '0.9rem', borderRadius: '4px' }}>
                  {error}
                </div>
              )}
              
              <div className="planner-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '25px' }}>
                {/* Name */}
                <div style={{ position: 'relative' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '8px' }}>{t('planner.labelName')}</label>
                  <div style={{ position: 'relative' }}>
                    <User size={18} style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary-gold)' }} />
                    <input 
                      type="text" 
                      name="name"
                      required
                      pattern="[A-Za-z\s]+"
                      title="Only letters and spaces are allowed"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('planner.phName')}
                      style={{
                        width: '100%', padding: '12px 15px 12px 45px', borderRadius: '8px', border: '1px solid rgba(181, 137, 37, 0.3)',
                        background: 'rgba(255, 255, 255, 0.7)', color: 'var(--text-light)', fontSize: '0.95rem', outline: 'none'
                      }}
                    />
                  </div>
                </div>

                {/* Email */}
                <div style={{ position: 'relative' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '8px' }}>{t('planner.labelEmail')}</label>
                  <div style={{ position: 'relative' }}>
                    <Mail size={18} style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary-gold)' }} />
                    <input 
                      type="email" 
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('planner.phEmail')}
                      style={{
                        width: '100%', padding: '12px 15px 12px 45px', borderRadius: '8px', border: '1px solid rgba(181, 137, 37, 0.3)',
                        background: 'rgba(255, 255, 255, 0.7)', color: 'var(--text-light)', fontSize: '0.95rem', outline: 'none'
                      }}
                    />
                  </div>
                </div>

                {/* Phone */}
                <div style={{ position: 'relative' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '8px' }}>{t('planner.labelPhone')}</label>
                  <div style={{ position: 'relative', display: 'flex', gap: '8px' }}>
                    <select 
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      style={{
                        padding: '12px', borderRadius: '8px', border: '1px solid rgba(181, 137, 37, 0.3)',
                        background: 'rgba(255, 255, 255, 0.7)', color: 'var(--text-light)', fontSize: '0.95rem', outline: 'none',
                        width: '100px'
                      }}
                    >
                      <option value="+91">+91 (IN)</option>
                      <option value="+1">+1 (US/CA)</option>
                      <option value="+44">+44 (UK)</option>
                      <option value="+61">+61 (AU)</option>
                      <option value="+971">+971 (AE)</option>
                      <option value="+49">+49 (DE)</option>
                      <option value="+33">+33 (FR)</option>
                      <option value="+39">+39 (IT)</option>
                      <option value="+34">+34 (ES)</option>
                      <option value="+65">+65 (SG)</option>
                      <option value="+60">+60 (MY)</option>
                      <option value="+81">+81 (JP)</option>
                      <option value="+86">+86 (CN)</option>
                      <option value="+27">+27 (ZA)</option>
                      <option value="+64">+64 (NZ)</option>
                      <option value="+55">+55 (BR)</option>
                      <option value="+52">+52 (MX)</option>
                      <option value="+31">+31 (NL)</option>
                      <option value="+41">+41 (CH)</option>
                      <option value="+46">+46 (SE)</option>
                      <option value="+972">+972 (IL)</option>
                    </select>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <Phone size={18} style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary-gold)' }} />
                      <input 
                        type="tel" 
                        name="phone"
                        required
                        minLength={phoneConstraints[countryCode]?.min || 7}
                        maxLength={phoneConstraints[countryCode]?.max || 15}
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t('planner.phPhone')}
                        style={{
                          width: '100%', padding: '12px 15px 12px 45px', borderRadius: '8px', border: '1px solid rgba(181, 137, 37, 0.3)',
                          background: 'rgba(255, 255, 255, 0.7)', color: 'var(--text-light)', fontSize: '0.95rem', outline: 'none'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Place */}
                <div style={{ position: 'relative' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '8px' }}>{t('planner.labelPlace')}</label>
                  <div style={{ position: 'relative' }}>
                    <MapPin size={18} style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary-gold)' }} />
                    <input 
                      type="text" 
                      name="place"
                      required
                      list="sacred-destinations"
                      value={formData.place}
                      onChange={handleChange}
                      placeholder={t('planner.phPlace')}
                      style={{
                        width: '100%', padding: '12px 15px 12px 45px', borderRadius: '8px', border: '1px solid rgba(181, 137, 37, 0.3)',
                        background: 'rgba(255, 255, 255, 0.7)', color: 'var(--text-light)', fontSize: '0.95rem', outline: 'none'
                      }}
                    />
                    <datalist id="sacred-destinations">
                      <option value="Rishikesh" />
                      <option value="Varanasi (Kashi)" />
                      <option value="Kedarnath" />
                      <option value="Badrinath" />
                      <option value="Haridwar" />
                      <option value="Tirupati Balaji" />
                      <option value="Bodh Gaya" />
                      <option value="Amritsar (Golden Temple)" />
                      <option value="Rameshwaram" />
                      <option value="Mathura & Vrindavan" />
                      <option value="Ayodhya" />
                      <option value="Puri (Jagannath Temple)" />
                      <option value="Somnath" />
                      <option value="Dwarka" />
                      <option value="Vaishno Devi" />
                      <option value="Shirdi" />
                      <option value="Madurai (Meenakshi Temple)" />
                      <option value="Ujjain (Mahakaleshwar)" />
                      <option value="Sarnath" />
                      <option value="Prayagraj (Allahabad)" />
                      <option value="Hampi" />
                      <option value="Thanjavur (Brihadeeswarar)" />
                      <option value="Kamakhya Temple" />
                      <option value="Guruvayur" />
                      <option value="Dharmasthala" />
                      <option value="Lumbini" />
                    </datalist>
                  </div>
                </div>

                {/* Days */}
                <div style={{ position: 'relative' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '8px' }}>{t('planner.labelDays')}</label>
                  <div style={{ position: 'relative' }}>
                    <Calendar size={18} style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary-gold)' }} />
                    <input 
                      type="number" 
                      name="days"
                      required
                      min="1"
                      max="365"
                      value={formData.days}
                      onChange={handleChange}
                      placeholder={t('planner.phDays')}
                      style={{
                        width: '100%', padding: '12px 15px 12px 45px', borderRadius: '8px', border: '1px solid rgba(181, 137, 37, 0.3)',
                        background: 'rgba(255, 255, 255, 0.7)', color: 'var(--text-light)', fontSize: '0.95rem', outline: 'none'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)' }}>{t('planner.labelIntentions')}</label>
                  <span style={{ fontSize: '0.7rem', color: (formData.description.trim() ? formData.description.trim().split(/\s+/).length : 0) >= 200 ? 'var(--accent-terracotta)' : 'var(--text-muted)', letterSpacing: '0.05em' }}>
                    {formData.description.trim() ? formData.description.trim().split(/\s+/).length : 0} / 200 words
                  </span>
                </div>
                <div style={{ position: 'relative' }}>
                  <PenTool size={18} style={{ position: 'absolute', left: '15px', top: '15px', color: 'var(--primary-gold)' }} />
                  <textarea 
                    name="description"
                    required
                    value={formData.description}
                    onChange={handleChange}
                    placeholder={t('planner.phIntentions')}
                    rows="5"
                    style={{
                      width: '100%', padding: '15px 15px 15px 45px', borderRadius: '8px', border: '1px solid rgba(181, 137, 37, 0.3)',
                      background: 'rgba(255, 255, 255, 0.7)', color: 'var(--text-light)', fontSize: '0.95rem', outline: 'none', resize: 'vertical'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="btn-gold pulse-gold-button"
                  style={{ 
                    padding: '14px 40px', 
                    borderRadius: '30px', 
                    fontSize: '0.9rem', 
                    letterSpacing: '0.15em', 
                    textTransform: 'uppercase', 
                    fontWeight: '600',
                    border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  {loading ? t('planner.btnSubmitting') : <><Compass size={18} /> {t('planner.btnSubmit')}</>}
                </button>
              </div>
              
              {!isLoggedIn && (
                <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '0.85rem', color: 'var(--accent-terracotta)' }}>
                  {t('planner.msgLoginReq')}
                </div>
              )}
            </form>
          )}
        </div>
      </div>
      
      <style>{`
        input, select, textarea {
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
          box-shadow: inset 0 2px 4px rgba(41, 33, 26, 0.02) !important;
        }
        input:focus, select:focus, textarea:focus {
          border-color: var(--primary-gold) !important;
          background: #ffffff !important;
          box-shadow: 0 0 0 3px rgba(181, 137, 37, 0.15), 0 4px 12px rgba(181, 137, 37, 0.08) !important;
        }
        ::placeholder {
          color: rgba(112, 97, 82, 0.5);
        }
        @media (max-width: 768px) {
          #planner {
            padding: 80px 0 40px !important;
            min-height: auto !important;
            align-items: flex-start !important;
          }
          .planner-form-grid {
            grid-template-columns: 1fr !important;
          }
          .planner-card {
            padding: 24px 16px !important;
          }
        }
      `}</style>
    </section>
  );
}
